import { Alert, AlertProps, AlertTitle, Box, Button, ButtonProps } from '@mui/material';
import { FC, useMemo } from 'react';

interface IAlertBoxProps {
	title?: React.ReactNode;
	children: React.ReactNode;
	button?: ButtonProps;
	severity?: AlertProps['severity'];
}

/**
 *
 * AlertBox is a component that displays a MUI Alert complete with a title, content, and a button.
 * The title and button are optional, and the content is required.
 * The button is displayed on the right side of the alert box for desktop, and below the content for mobile.
 *
 * @param title - The optional title of the alert box
 * @param children - The content of the alert box
 * @param button - The optional button object to display in the alert box
 * @param severity - The colour and icon of the alert box
 */

const AlertBox: FC<IAlertBoxProps> = ({ title, children, button, severity }) => {
	const gridTemplateColumns = useMemo(() => ({ xs: '1fr', lg: button ? '1fr min-content' : '1fr' }), [button]);
	const order = useMemo(() => ({ xs: 3, lg: title ? 2 : 4 }), [title]);

	const { sx: buttonSx, children: buttonChildren, ...buttonProps } = button || {};

	return (
		<Alert severity={severity}>
			<Box sx={{ display: 'grid', gridTemplateColumns }}>
				{title && <AlertTitle sx={{ order: 1 }}>{title}</AlertTitle>}
				<Box sx={{ order: 3 }}>{children}</Box>
				{button && (
					<Box
						sx={{
							order,
							display: 'flex',
							justifyContent: 'center',
							mt: { xs: 2, lg: 0 },
							gridRow: { xs: 'unset', lg: 'span 2' },
						}}
					>
						<Button
							sx={{
								height: '2.1rem',
								display: 'block',
								backgroundColor: 'white',
								color: 'primary.main',
								border: 'none',
								px: 1.2,
								'&:hover': {
									backgroundColor: 'grey.200',
								},
								...buttonSx,
							}}
							variant='outlined'
							{...buttonProps}
						>
							{buttonChildren}
						</Button>
					</Box>
				)}
			</Box>
		</Alert>
	);
};

export default AlertBox;
